import React, { useState } from 'react';
import DeviceSetup from '../../video_meeting/components/DeviceSetup';
import { Check, Monitor, Mic, Volume2, AlertCircle } from 'lucide-react';
import { PublicTopNavigation } from './PublicTopNavigation';
import { useCurrentUser } from '../../hooks/useCurrentUser';

export const TestVideo: React.FC = () => {
  const { currentUser } = useCurrentUser();
  const [setupComplete, setSetupComplete] = useState(false);
  const [deviceSettings, setDeviceSettings] = useState<{
    videoDeviceId: string;
    audioInputDeviceId: string;
    audioOutputDeviceId: string;
  } | null>(null);

  const handleSetupComplete = (settings: {
    videoDeviceId: string;
    audioInputDeviceId: string;
    audioOutputDeviceId: string;
  }) => {
    setDeviceSettings(settings);
    setSetupComplete(true);
  };

  const handleTestAgain = () => {
    setSetupComplete(false);
    setDeviceSettings(null);
  };

  if (setupComplete && deviceSettings) {
    return (
      <>
        <PublicTopNavigation currentUser={currentUser || undefined} />
        <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
          <div className="bg-white shadow-xl rounded-lg overflow-hidden">
            {/* Success Header */}
            <div className="bg-green-50 border-b border-green-100 px-6 py-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Device Test Successful!
              </h2>
              <p className="text-gray-600 max-w-md mx-auto">
                Your camera, microphone, and speakers are set up correctly. You're ready to join video meetings.
              </p>
            </div>

            {/* Device Summary */}
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Selected Devices</h3>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Monitor className="h-5 w-5 text-gray-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Camera</p>
                    <p className="text-sm text-gray-600">Device configured and saved</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Mic className="h-5 w-5 text-gray-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Microphone</p>
                    <p className="text-sm text-gray-600">Device configured and saved</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Volume2 className="h-5 w-5 text-gray-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Speakers</p>
                    <p className="text-sm text-gray-600">Device configured and saved</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="ml-3">
                      <p className="text-sm text-blue-900 font-medium">Settings Saved</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Your device preferences have been saved to your browser and will be used for future meetings on this site.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  onClick={handleTestAgain}
                  className="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Test Devices Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <PublicTopNavigation currentUser={currentUser || undefined} />
      <div className="min-h-screen bg-gray-50 pt-16">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900">Test Your Video & Audio</h1>
              <p className="mt-2 text-lg text-gray-600">
                Make sure your camera, microphone, and speakers are working before joining a meeting
              </p>
            </div>
          </div>
        </div>

      {/* Main Content */}
      <div className="py-8">
        <DeviceSetup onSetupComplete={handleSetupComplete} isTestMode={true} />
      </div>

      {/* Information Footer */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-2">Why test your devices?</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start">
              <span className="block w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
              <span>Ensure your camera is working and properly positioned</span>
            </li>
            <li className="flex items-start">
              <span className="block w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
              <span>Verify your microphone is picking up sound clearly</span>
            </li>
            <li className="flex items-start">
              <span className="block w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
              <span>Test your speakers to make sure you can hear others</span>
            </li>
            <li className="flex items-start">
              <span className="block w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
              <span>Save your device preferences for future meetings</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
    </>
  );
};